import { FollowerTemplateSchema, type FollowerTemplate } from '../../schemas/follower';

/**
 * Template **Animare Zombi dello Sciamano** (manuale V1, rr. 5599-5618).
 *
 * 4° template Follower del sistema FI-8 (dopo Compagno Animale, Famiglio,
 * Supporto Elementale). Esercita gli stessi pattern di D8a del Druido ma
 * in forma **minimale**:
 *
 *  - **`recruitRoll`**: tira+CAR (con modificatori di *Aiuto dall'Altro
 *    Mondo*, applicati al tavolo dal giocatore) → 10+ concede tutte e 4 le
 *    scelte; 7-9 ne concede 2.
 *  - **2 dei 5 kind D8a usati**:
 *      - `modifier-from-narrative` («un modificatore di Caratteristica a +2»,
 *        NON cumulativa: il manuale non prevede "+3 se due volte" come per
 *        il Druido — `allowDuplicate: false`)
 *      - `hp-bonus-scaling` («resistente»: +2 PF×lvl, max +20)
 *      - `narrative-trait` × 2 («cervello funzionante», «non sembra cadavere»)
 *
 *  - **Nessun costo strutturato** (`costMotivation: undefined`): il manuale
 *    descrive solo il «blood-cost narrativo» per curare lo zombi (il Sciamano
 *    subisce danni = PF curati). Runtime di sessione, description-only.
 *  - **Nessun `archetypeTagBundle`**: lo zombi non ha varianti elementali.
 *
 * **Stats fisse** (manuale r. 5603): livello 2, modificatori tutti a +1,
 * 1 PF (hpRule fixed), Carico 6, d6.
 *
 * Limite "1 zombi per volta" è description-only nel testo della mossa
 * (runtime Fase F).
 */
export const sciamanoAnimareZombiTemplate: FollowerTemplate = FollowerTemplateSchema.parse({
  id: 'sciamano-animare-zombi',
  sourceMoveId: 'sciamano.animare-zombi',
  type: 'undead',
  label: 'Zombi',
  baseStats: {
    level: 2,
    modifiers: {
      forza: 1,
      destrezza: 1,
      costituzione: 1,
      intelligenza: 1,
      saggezza: 1,
      carisma: 1,
    },
    hpRule: { type: 'fixed', value: 1 },
    damageDie: 'd6',
    load: 6,
    tags: ['non-morto'],
  },
  fixedSkills: [],
  fixedTraits: [],
  recruitRoll: {
    stat: 'carisma',
    onSuccess: { choicesCount: 4 },
    onPartial: { choicesCount: 2 },
  },
  peculiaritiesFromChoiceGroup: {
    fromChoiceGroup: 'main',
    optionToEffect: {
      'mod-piu-2': {
        kind: 'modifier-from-narrative',
        narrativeFieldId: 'quale-mod-zombi',
        baseAmount: 2,
      },
      resistente: {
        kind: 'hp-bonus-scaling',
        perCharacterLevel: 2,
        max: 20,
      },
      'cervello-funzionante': {
        kind: 'narrative-trait',
        traitId: 'zombi-cervello-funzionante',
        label: 'Cervello funzionante',
        description: 'Lo zombi può eseguire ordini complessi (non solo gesti elementari).',
      },
      'non-sembra-cadavere': {
        kind: 'narrative-trait',
        traitId: 'zombi-non-sembra-cadavere',
        label: 'Non sembra un cadavere',
        description: 'Per un paio di giorni può passare per vivo agli occhi più disattenti.',
      },
    },
  },
  nameFromNarrativeField: 'nome-zombi',
  respawnRule:
    "Lo zombi dura finché non viene portato a 0 PF o la magia non termina. Per crearne uno nuovo, spendi una presa di *Aiuto dall'Altro Mondo* e tira di nuovo.",
});
